<template>
  <div>
    <sidebar></sidebar>
    <div :style="{ 'margin-left': sidebarwidth }">
      <div class="max-w-lg mx-auto px-5 py-10">
        <div class="bg-white rounded-lg shadow p-8">
          <h2 class="text-2xl font-bold mb-1">Change Password</h2>
          <p class="text-sm text-gray-500 mb-6">Update your account password.</p>

          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Current Password <span class="text-red-500">*</span></label>
            <div class="relative">
              <input
                v-model="form.current_password"
                :type="showCurrentPassword ? 'text' : 'password'"
                placeholder="Enter current password"
                class="w-full border border-gray-300 rounded px-3 py-2 pr-10 text-sm focus:outline-none focus:border-blue-500"
              />
              <button
                type="button"
                @click="showCurrentPassword = !showCurrentPassword"
                class="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-black"
                aria-label="Toggle current password visibility"
              >
                <i :class="showCurrentPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
              </button>
            </div>
          </div>

          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">New Password <span class="text-red-500">*</span></label>
            <div class="relative">
              <input
                v-model="form.new_password"
                :type="showNewPassword ? 'text' : 'password'"
                placeholder="Min 8 characters"
                class="w-full border border-gray-300 rounded px-3 py-2 pr-10 text-sm focus:outline-none focus:border-blue-500"
              />
              <button
                type="button"
                @click="showNewPassword = !showNewPassword"
                class="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-black"
                aria-label="Toggle new password visibility"
              >
                <i :class="showNewPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
              </button>
            </div>
          </div>

          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-1">Confirm New Password <span class="text-red-500">*</span></label>
            <div class="relative">
              <input
                v-model="form.confirm_password"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="Re-enter new password"
                class="w-full border border-gray-300 rounded px-3 py-2 pr-10 text-sm focus:outline-none focus:border-blue-500"
              />
              <button
                type="button"
                @click="showConfirmPassword = !showConfirmPassword"
                class="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-black"
                aria-label="Toggle confirm password visibility"
              >
                <i :class="showConfirmPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
              </button>
            </div>
          </div>

          <p v-if="errorMsg" class="text-red-500 text-sm mb-4">{{ errorMsg }}</p>
          <p v-if="successMsg" class="text-green-600 text-sm mb-4">{{ successMsg }}</p>

          <button
            @click="changePassword"
            :disabled="loading"
            class="w-full bg-black text-white font-bold py-2 rounded hover:bg-gray-800 disabled:opacity-60"
          >
            {{ loading ? 'Updating...' : 'Update Password' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import sidebar from "@/components/sidebar/Sidebar.vue";
import { sidebarwidth } from "@/store/index.js";
import api from "../../boot/axios.js";

const router = useRouter();

const form = ref({
  current_password: "",
  new_password: "",
  confirm_password: "",
});
const loading    = ref(false);
const errorMsg   = ref("");
const successMsg = ref("");
const showCurrentPassword = ref(false);
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);

const changePassword = async () => {
  errorMsg.value   = "";
  successMsg.value = "";

  if (!form.value.current_password || !form.value.new_password || !form.value.confirm_password) {
    errorMsg.value = "All fields are required.";
    return;
  }
  if (form.value.new_password !== form.value.confirm_password) {
    errorMsg.value = "New passwords do not match.";
    return;
  }
  if (form.value.new_password.length < 8) {
    errorMsg.value = "New password must be at least 8 characters.";
    return;
  }

  loading.value = true;
  try {
    const res = await api.post("/auth/change-password", {
      current_password: form.value.current_password,
      new_password:     form.value.new_password,
      confirm_password: form.value.confirm_password,
    });

    if (res.data.status === true) {
      successMsg.value = "Password changed successfully. Please log in again.";
      // Backend invalidates all tokens on password change → force logout
      setTimeout(() => {
        localStorage.clear();
        router.push("/");
      }, 2000);
    } else {
      errorMsg.value = res.data.message || "Failed to change password.";
    }
  } catch (err) {
    errorMsg.value = err?.response?.data?.message || "Something went wrong.";
  } finally {
    loading.value = false;
  }
};
</script>